import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, ThumbsUp, ThumbsDown, MessageCircle, ShieldCheck } from "lucide-react";

export default function ReviewPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [name, setName] = useState("");
  const [comment, setComment] = useState("");
  const [posting, setPosting] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/review/${token}`);
      setData(data);
      setName(data.reviewer_name || "");
    } catch (e) {
      setError(e?.response?.data?.detail || "Link invalid or expired");
    }
  };
  useEffect(() => { load(); }, [token]);

  const submit = async (decision) => {
    if (!name.trim()) { toast.error("Please enter your name"); return; }
    if (!comment.trim() && decision === "comment") { toast.error("Add a comment"); return; }
    setPosting(true);
    try {
      await axios.post(`${API}/review/${token}/comment`, {
        reviewer_name: name,
        comment: comment || (decision === "approve" ? "Approved." : decision === "reject" ? "Rejected." : ""),
        decision,
      });
      toast.success(decision === "approve" ? "Approved" : decision === "reject" ? "Rejected" : "Comment posted");
      setComment("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to post");
    } finally {
      setPosting(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-ink text-bone flex items-center justify-center px-6">
        <div className="border border-hairline p-10 max-w-md text-center">
          <ShieldCheck className="w-8 h-8 text-bone/30 mx-auto mb-4" />
          <h1 className="font-display text-2xl font-bold tracking-tightest">{error}</h1>
          <p className="mt-3 text-bone/50 text-sm">Ask the admin to send you a fresh review link.</p>
        </div>
      </div>
    );
  }
  if (!data) return null;
  const a = data.article;

  return (
    <div className="min-h-screen bg-ink text-bone">
      <header className="sticky top-0 z-30 bg-ink/85 backdrop-blur-xl border-b border-hairline">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center gap-3">
          <div className="w-8 h-8 bg-safety flex items-center justify-center">
            <span className="font-display font-black text-white text-sm">L</span>
          </div>
          <div className="flex-1">
            <div className="font-display font-bold tracking-tightest leading-none">Lingent · Review</div>
            <div className="label-mono text-[10px] text-bone/50 leading-none mt-1">WiseWit Solutions · stakeholder view</div>
          </div>
          <span className="label-mono px-2 py-1 bg-yellow-500/20 text-yellow-300 border border-yellow-500/40 text-[10px]">
            {a.status}
          </span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        <div className="label-mono text-safety">// reviewing draft</div>
        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tightest mt-3" data-testid="review-article-title">
          {a.title}
        </h1>
        <div className="mt-2 label-mono text-bone/50 text-[10px]">{a.category}</div>

        {a.cover_image && (
          <img src={a.cover_image} alt="" className="mt-6 w-full border border-hairline" />
        )}

        <article className="mt-6 prose prose-invert max-w-none text-bone/85 whitespace-pre-wrap font-sans leading-relaxed" data-testid="review-article-body">
          {a.body}
        </article>

        <div className="mt-6 flex flex-wrap gap-2">
          {(a.hashtags || []).map((h, i) => (
            <Badge key={i} className="rounded-none bg-klein text-white label-mono">{h}</Badge>
          ))}
        </div>

        <section className="mt-12 border-t border-hairline pt-8">
          <h2 className="font-display text-2xl font-bold tracking-tightest mb-4">Your feedback</h2>
          <div className="space-y-4">
            <div>
              <Label className="label-mono text-bone/70">Your name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jane Doe"
                className="mt-2 rounded-none bg-surface border-hairline focus-visible:ring-1 focus-visible:ring-klein h-11"
                data-testid="review-name-input"
              />
            </div>
            <div>
              <Label className="label-mono text-bone/70">Comment (optional for approve/reject)</Label>
              <Textarea
                rows={4}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Suggest edits, point out factual issues, or just say it looks good."
                className="mt-2 rounded-none bg-surface border-hairline focus-visible:ring-1 focus-visible:ring-klein"
                data-testid="review-comment-input"
              />
            </div>
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={() => submit("approve")}
                disabled={posting}
                data-testid="review-approve-btn"
                className="rounded-none bg-klein hover:bg-klein-hover text-white label-mono h-11"
              >
                {posting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ThumbsUp className="w-4 h-4 mr-2" />}
                Approve
              </Button>
              <Button
                onClick={() => submit("reject")}
                disabled={posting}
                variant="outline"
                data-testid="review-reject-btn"
                className="rounded-none border-hairline bg-transparent hover:bg-red-500 hover:text-white label-mono h-11"
              >
                <ThumbsDown className="w-4 h-4 mr-2" /> Reject
              </Button>
              <Button
                onClick={() => submit("comment")}
                disabled={posting}
                variant="outline"
                data-testid="review-comment-btn"
                className="rounded-none border-hairline bg-transparent hover:bg-bone hover:text-ink label-mono h-11"
              >
                <MessageCircle className="w-4 h-4 mr-2" /> Comment only
              </Button>
            </div>
          </div>
        </section>

        {data.comments?.length > 0 && (
          <section className="mt-12 border-t border-hairline pt-8">
            <h2 className="font-display text-xl font-bold tracking-tightest mb-4">
              Feedback so far · {data.comments.length}
            </h2>
            <div className="space-y-3">
              {data.comments.map((c) => (
                <div key={c.id} className="border border-hairline bg-surface p-4">
                  <div className="flex items-center justify-between">
                    <div className="font-display font-bold tracking-tightest">{c.reviewer_name}</div>
                    <span className={`label-mono px-2 py-0.5 text-[10px] ${
                      c.decision === "approve" ? "bg-klein text-white"
                      : c.decision === "reject" ? "bg-red-500/30 text-red-200"
                      : "bg-surface-2 text-bone/70"
                    }`}>{c.decision}</span>
                  </div>
                  <p className="mt-2 text-bone/80 text-sm">{c.comment}</p>
                  <div className="mt-1 label-mono text-[10px] text-bone/40">{String(c.created_at).slice(0, 16)}</div>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
